/**
 * `search`: find anything in any project you can read (RFC 0336 S2).
 *
 * `list_sessions` and `list_documents` list one project, the session's own,
 * which is why an agent could not find work anywhere else. This reads every
 * project's catalog the World lists and returns the same rows ⌘K shows. It
 * only reads, and it opens no document or session to answer, so it is safe
 * to call freely. Acting on a result needs nothing but its id (S1).
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import {
  searchWorld,
  type WorldResult,
  type WorldResultKind,
} from '../model/world/search'
import { type ToolDef, toolArgString } from './project-tools'

const KINDS: WorldResultKind[] = ['document', 'session', 'task', 'app']

/** A title as one short line: some catalogs hold a whole paragraph there. */
function short(text: string, max = 120): string {
  const first = text.split('\n')[0]?.trim() ?? ''
  return first.length > max ? `${first.slice(0, max - 1)}…` : first
}

function line(r: WorldResult): string {
  const hint = r.hint ? `  (${short(r.hint, 60)})` : ''
  // The project id rides at the end: start_session needs it to open new
  // work there, and nothing else does.
  return `${r.id}  ${r.kind}  ${short(r.title)}${hint}  · ${r.project} ${r.projectId}`
}

export function searchTools(
  repo: Repo,
  projectId: string,
  opts: { worldId?: string | null } = {},
): Record<string, ToolDef> {
  return {
    search: {
      description:
        'Search every project you can read for documents, sessions, tasks and ' +
        'apps by title, as "id  kind  title  · project projectId" lines, most relevant ' +
        'first. Read-only and cheap. Empty text lists what changed most ' +
        'recently. Act on a result by its id with the other tools; they find ' +
        'its project themselves.',
      inputSchema: {
        type: 'object',
        properties: {
          text: { type: 'string', description: 'What to look for.' },
          kind: {
            type: 'string',
            enum: KINDS,
            description: 'Only this kind of thing.',
          },
          limit: {
            type: 'number',
            description: 'Rows per page (default 20, at most 100).',
          },
          cursor: {
            type: 'string',
            description: 'The cursor a previous page returned, for the next.',
          },
        },
      },
      execute: async (a) => {
        const kind = toolArgString(a.kind)?.trim() as
          | WorldResultKind
          | undefined
        if (kind && !KINDS.includes(kind)) {
          throw new Error(`kind must be one of ${KINDS.join(', ')}`)
        }
        const page = await searchWorld(repo, opts.worldId, projectId, {
          text: toolArgString(a.text) ?? '',
          ...(kind ? { kind } : {}),
          ...(typeof a.limit === 'number' ? { limit: a.limit } : {}),
          ...(toolArgString(a.cursor)
            ? { cursor: toolArgString(a.cursor) }
            : {}),
        })
        if (page.results.length === 0) return '(nothing found)'
        const more = page.cursor ? `\n(more: cursor ${page.cursor})` : ''
        return page.results.map(line).join('\n') + more
      },
    },
  }
}
